import {injectable} from "inversify";
import {LeagueDivision, Matchday} from "@/modules/competitions/entity";
import {Match} from "@/modules/match/entity";
import {TableStatistics} from "@/modules/competitions/entity/TableStatistics";
import {MatchResultEnum} from "@/modules/base/enums";
import {FindManyOptions} from "typeorm";

@injectable()
export default class TableStatisticsGenerator {
  public async increment(matchday: Matchday) {
    await matchday.reload();
    await matchday.load('matches');

    if (!matchday.finished) {
      throw 'Can not generate stats for unfinished matchday';
    }

    await matchday.load('division');

    const stats = await TableStatistics.find({
      relations: {
        team: true,
        division: true,
        season: true,
      },
      where: {
        season: {
          current: true,
        },
        division: {
          id: matchday.division.id,
        },
      }
    } as FindManyOptions);

    const matches = await Match
      .createQueryBuilder('match')
      .innerJoinAndSelect('match.matchday', 'matchday')
      .innerJoinAndSelect('match.homeTeam', 'homeTeam')
      .innerJoinAndSelect('match.awayTeam', 'awayTeam')
      .where('matchday.id = :matchdayId', {matchdayId: matchday.id})
      .getMany();

    await matches.forEachAsync(async (match: Match) => {
      if (!match.result) {
        throw 'Match is not finished.';
      }

      const homeStat = stats.find(s => s.team.equals(match.homeTeam));
      const awayStat = stats.find(s => s.team.equals(match.awayTeam));

      if (!homeStat || !awayStat) {
        throw 'There are table stats entries missing.';
      }

      switch (match.result) {
        case MatchResultEnum.HOME_WIN:
          homeStat.points += 3;
          homeStat.wins++;
          awayStat.loses++;
          break;
        case MatchResultEnum.AWAY_WIN:
          awayStat.points += 3;
          homeStat.loses++;
          awayStat.wins++;
          break;
        case MatchResultEnum.DRAW:
          homeStat.points++;
          awayStat.points++;
          homeStat.draws++;
          awayStat.draws++;
          break;
      }

      homeStat.matches++;
      awayStat.matches++;
      homeStat.goals += match.homeGoals!;
      awayStat.goals += match.awayGoals!;
      homeStat.goals_against += match.awayGoals!;
      awayStat.goals_against += match.homeGoals!;

      await homeStat.save();
      await awayStat.save();
    });

    await (matchday.division as LeagueDivision).load('stats');

    const divisionStats = await TableStatistics
      .createQueryBuilder('stats')
      .innerJoin('stats.season', 'season')
      .where('stats.divisionId = :id', {id: matchday.division.id})
      .andWhere('season.current = true')
      .getMany();

    await collect(divisionStats)
      .sort((a, b) => {
        if (a.points > b.points) {
          return -1;
        } else if (a.points < b.points) {
          return 1;
        } else {

          if (a.goalsDiff > b.goalsDiff) {
            return -1;
          } else if (a.goalsDiff < b.goalsDiff) {
            return 1;
          } else {

            if (a.goals > b.goals) {
              return -1;
            } else if (a.goals < b.goals) {
              return 1;
            } else {
              return 0;
            }
          }
        }
      })
      .toArray()
      .forEachAsync(async (stat: TableStatistics, index) => {
        stat.rank = index + 1;
        await stat.save();
      });
  }
}
